import {
  ArrowLeft,
  ArrowRight,
  Confetti,
  House,
  Plus,
  SignOut,
  Sparkle,
  Ticket,
  Trash,
  UsersThree,
} from '@phosphor-icons/react';
import { personColors, type Location, type PersonColor } from '@shh/shared';
import {
  Avatar,
  Button,
  ChoiceGroup,
  Heading,
  Icon,
  IconButton,
  Surface,
  Text,
  TextField,
  cn,
} from '@shh/ui';
import { signOut } from 'firebase/auth';
import { useState, type ReactNode } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { acceptInvite, createHousehold } from '../../data/api';
import { firebase } from '../../lib/firebase';
import { PlaceField } from '../../components/PlaceField';
import { DeleteAccountButton } from '../../components/DeleteAccountSheet';
import { InviteHint } from '../../components/InviteHint';
import { testFamilyText } from '../../i18n/admin';
import { readInvite } from '../../lib/invite';
import {
  colorNames,
  kindToProfile,
  nextFreeColor,
  personKinds,
  type PersonKind,
} from '../../lib/people';
import { locale } from '../../lib/locale';
import { startText } from '../../i18n/start';
import { fill } from '../../i18n/fill';

type Mode = 'choose' | 'new' | 'invite' | 'demo';
interface Draft {
  key: number;
  name: string;
  kind: PersonKind;
  color: PersonColor;
}

/**
 * First start after signing in. Short steps, one decision each, defaults everywhere, the
 * important button always in the thumb zone. Nobody starts with an empty app.
 */
export function SetupPage() {
  const [mode, setMode] = useState<Mode>('choose');
  return (
    <main className="mx-auto flex min-h-dvh max-w-narrow flex-col gap-section px-safe pt-section pb-safe">
      {mode === 'choose' && <Choose onChoose={setMode} />}
      {mode === 'new' && <NewFamily onBack={() => setMode('choose')} />}
      {mode === 'demo' && <Demo onBack={() => setMode('choose')} />}
      {mode === 'invite' && <Invite onBack={() => setMode('choose')} />}
    </main>
  );
}

function Choose({ onChoose }: { onChoose: (m: Mode) => void }) {
  const options: { mode: Mode; icon: typeof House; title: string; text: string }[] = [
    {
      mode: 'new',
      icon: House,
      ...startText.setupNew,
    },
    {
      mode: 'invite',
      icon: Ticket,
      ...startText.setupInvite,
    },
    {
      mode: 'demo',
      icon: Sparkle,
      ...startText.setupDemo,
    },
  ];
  return (
    <>
      <header className="flex flex-col gap-gap-sm">
        <Heading level={1} size="xl">
          {startText.hi}
        </Heading>
        <Text tone="secondary">{startText.howStart}</Text>
      </header>
      {readInvite() && <InviteHint>{testFamilyText.invitedSetup}</InviteHint>}
      <ul className="flex flex-col gap-gap-md">
        {options.map((o) => (
          <li key={o.mode}>
            <AriaButton
              onPress={() => onChoose(o.mode)}
              className="flex w-full cursor-pointer items-center gap-gap-md rounded-card border-(length:--shh-card-border) border-border-card bg-surface-raised p-inset-md text-left shadow-card outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-hovered:bg-action-quiet-hover data-pressed:scale-98"
            >
              <span className="grid size-touch-target shrink-0 place-items-center rounded-control bg-accent-secondary-soft text-accent-secondary">
                <Icon icon={o.icon} size="lg" />
              </span>
              <span className="flex flex-1 flex-col">
                <span className="font-display text-lg font-black">{o.title}</span>
                <span className="text-sm text-text-secondary">{o.text}</span>
              </span>
              <Icon icon={ArrowRight} size="md" className="text-text-muted" />
            </AriaButton>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-gap-sm">
        <Button variant="quiet" icon={SignOut} onPress={() => void signOut(firebase().auth)}>
          {startText.signOut}
        </Button>
        <DeleteAccountButton />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------

function ColorPicker({
  name,
  value,
  onChange,
  taken,
}: {
  name: string;
  value: PersonColor;
  onChange: (c: PersonColor) => void;
  taken: PersonColor[];
}) {
  return (
    <ChoiceGroup
      label={startText.color}
      layout="tile"
      value={value}
      onChange={onChange}
      choices={personColors.map((c) => ({
        value: c,
        label: colorNames[c],
        description: taken.includes(c) && c !== value ? startText.taken : undefined,
        picture: <Avatar name={name || '?'} color={c} size="sm" decorative />,
      }))}
    />
  );
}

function StepFrame({
  step,
  total,
  title,
  children,
  onBack,
  onNext,
  nextLabel = startText.next,
  nextDisabled,
  busy,
}: {
  step: number;
  total: number;
  title: string;
  children: ReactNode;
  onBack: () => void;
  onNext: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  busy?: boolean;
}) {
  return (
    <>
      <header className="flex flex-col gap-gap-sm">
        <div className="flex gap-gap-xs" aria-hidden>
          {Array.from({ length: total }, (_, i) => (
            <span
              key={i}
              className={cn(
                'h-1.5 flex-1 rounded-chip',
                i <= step ? 'bg-action-primary' : 'bg-border-subtle',
              )}
            />
          ))}
        </div>
        <Text size="sm" tone="muted">
          {startText.stepOf(String(step + 1), String(total))}
        </Text>
        <Heading level={1} size="lg">
          {title}
        </Heading>
      </header>
      <div className="flex flex-1 flex-col gap-gap-lg">{children}</div>
      {/* Thumb zone */}
      <div className="sticky bottom-0 grid grid-cols-[auto_1fr] gap-gap-sm bg-surface-canvas py-inset-sm">
        <IconButton
          icon={ArrowLeft}
          variant="secondary"
          size="lg"
          aria-label={startText.back}
          onPress={onBack}
        />
        <Button
          size="lg"
          onPress={onNext}
          isDisabled={nextDisabled || busy}
          icon={busy ? undefined : step + 1 === total ? Confetti : undefined}
        >
          {busy ? startText.settingUp : nextLabel}
        </Button>
      </div>
    </>
  );
}

function NewFamily({ onBack }: { onBack: () => void }) {
  const [step, setStep] = useState(0);
  const [family, setFamily] = useState('');
  const [place, setPlace] = useState<Location>();
  const [me, setMe] = useState<{ name: string; color: PersonColor }>({ name: '', color: 'clay' });
  const [others, setOthers] = useState<Draft[]>([]);
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [starter, setStarter] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const used = [me.color, ...others.map((o) => o.color)];

  const addPerson = () =>
    setOthers((o) => [
      ...o,
      { key: Date.now(), name: '', kind: 'kid', color: nextFreeColor(used) },
    ]);
  const update = (key: number, patch: Partial<Draft>) =>
    setOthers((o) => o.map((d) => (d.key === key ? { ...d, ...patch } : d)));

  const finish = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await createHousehold({
        name: family.trim() || startText.defaultFamily(me.name),
        location: place,
        me: { name: me.name.trim(), role: 'parent', ageProfile: 'teen', color: me.color },
        others: others
          .filter((o) => o.name.trim())
          .map((o) => ({ name: o.name.trim(), color: o.color, ...kindToProfile(o.kind) })),
        parentPin: pin,
        starterPack: starter,
        demo: false,
        locale, // Familiensprache starts as the language of this device
      });
    } catch {
      setError(startText.failed);
      setBusy(false);
    }
  };

  const steps = [
    {
      title: startText.familyAsk,
      ok: family.trim().length > 0,
      body: (
        <>
          <TextField
            label={startText.familyName}
            size="lg"
            placeholder={startText.familyExample}
            value={family}
            onChange={setFamily}
            autoFocus
          />
          <PlaceField value={place} onChange={setPlace} />
        </>
      ),
    },
    {
      title: startText.whoAreYou,
      ok: me.name.trim().length > 0,
      body: (
        <>
          <div className="flex items-center gap-gap-md">
            <Avatar name={me.name || '?'} color={me.color} size="lg" decorative />
            <TextField
              label={startText.yourName}
              size="lg"
              value={me.name}
              onChange={(name) => setMe((m) => ({ ...m, name }))}
              className="flex-1"
              autoFocus
            />
          </div>
          <ColorPicker
            name={me.name}
            value={me.color}
            onChange={(color) => setMe((m) => ({ ...m, color }))}
            taken={used}
          />
        </>
      ),
    },
    {
      title: startText.whoElse,
      ok: others.every((o) => o.name.trim()),
      body: (
        <>
          <Text tone="secondary">{startText.namesOnly}</Text>
          <ul className="flex flex-col gap-gap-md">
            {others.map((o) => (
              <li key={o.key}>
                <Surface className="flex flex-col gap-gap-md">
                  <div className="flex items-end gap-gap-md">
                    <Avatar name={o.name || '?'} color={o.color} size="md" decorative />
                    <TextField
                      label={startText.name}
                      value={o.name}
                      onChange={(name) => update(o.key, { name })}
                      className="flex-1"
                      autoFocus
                    />
                    <IconButton
                      icon={Trash}
                      aria-label={startText.removePerson(o.name || startText.person)}
                      onPress={() => setOthers((x) => x.filter((d) => d.key !== o.key))}
                    />
                  </div>
                  <ChoiceGroup
                    label={startText.whoIsThis}
                    value={o.kind}
                    onChange={(kind) => update(o.key, { kind })}
                    choices={personKinds}
                  />
                </Surface>
              </li>
            ))}
          </ul>
          <Button variant="secondary" icon={Plus} size="lg" onPress={addPerson}>
            {startText.addPerson}
          </Button>
        </>
      ),
    },
    {
      title: startText.pinTitle,
      ok: /^\d{4,6}$/.test(pin) && pin === pin2,
      body: (
        <>
          <Text tone="secondary">{startText.pinText}</Text>
          <TextField
            label={startText.pin}
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            value={pin}
            onChange={(v) => setPin(v.replace(/\D/g, '').slice(0, 6))}
          />
          <TextField
            label={startText.pinAgain}
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            value={pin2}
            onChange={(v) => setPin2(v.replace(/\D/g, '').slice(0, 6))}
            isInvalid={pin2.length >= 4 && pin !== pin2}
            errorMessage={startText.pinsDiffer}
          />
          <ChoiceGroup
            label={startText.start}
            value={starter ? 'yes' : 'no'}
            onChange={(v) => setStarter(v === 'yes')}
            choices={[
              {
                value: 'yes',
                label: startText.withTemplates,
                description: startText.withTemplatesHint,
              },
              { value: 'no', label: startText.empty, description: startText.emptyHint },
            ]}
          />
          {error && (
            <p role="alert" className="font-semibold text-feedback-danger">
              {error}
            </p>
          )}
        </>
      ),
    },
  ];
  const s = steps[step]!;

  return (
    <StepFrame
      step={step}
      total={steps.length}
      title={s.title}
      onBack={() => (step === 0 ? onBack() : setStep(step - 1))}
      onNext={() => (step + 1 === steps.length ? void finish() : setStep(step + 1))}
      nextLabel={step + 1 === steps.length ? startText.finish : startText.next}
      nextDisabled={!s.ok}
      busy={busy}
    >
      {s.body}
    </StepFrame>
  );
}

function Demo({ onBack }: { onBack: () => void }) {
  const [name, setName] = useState('');
  const [as, setAs] = useState<'mama' | 'papa' | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  return (
    <StepFrame
      step={0}
      total={1}
      title={startText.demoTitle}
      onBack={onBack}
      busy={busy}
      nextDisabled={!name.trim() || !as}
      nextLabel={startText.demoLoad}
      onNext={() => {
        setBusy(true);
        void createHousehold({
          name: startText.demoFamily,
          // Hessen: the example family shows school holidays and public holidays (1.24)
          location: { name: 'Kassel', latitude: 51.32, longitude: 9.5, region: 'HE' },
          me: { name: name.trim(), role: 'parent', ageProfile: 'teen', color: 'clay' },
          others: [],
          parentPin: '1234',
          starterPack: true,
          demo: true,
          demoAs: as || 'mama',
          locale,
        }).catch(() => {
          setError(startText.failed);
          setBusy(false);
        });
      }}
    >
      <Text tone="secondary">{fill(startText.demoText, <strong>1234</strong>)}</Text>
      <ChoiceGroup
        label={startText.whoAreYouShort}
        value={as}
        onChange={(v) => setAs(v as 'mama' | 'papa')}
        choices={[
          { value: 'mama', label: startText.mum },
          { value: 'papa', label: startText.dad },
        ]}
      />
      <TextField label={startText.firstName} size="lg" value={name} onChange={setName} />
      <Text size="sm" tone="muted" className="flex items-center gap-gap-xs">
        <Icon icon={UsersThree} size="sm" /> {startText.demoLater}
      </Text>
      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {error}
        </p>
      )}
    </StepFrame>
  );
}

function Invite({ onBack }: { onBack: () => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  return (
    <StepFrame
      step={0}
      total={1}
      title={startText.inviteTitle}
      onBack={onBack}
      busy={busy}
      nextDisabled={code.trim().length < 6}
      nextLabel={startText.join}
      onNext={() => {
        setBusy(true);
        setError(undefined);
        void acceptInvite(code).catch(() => {
          setError(startText.badCode);
          setBusy(false);
        });
      }}
    >
      <Text tone="secondary">{startText.inviteHow}</Text>
      <TextField
        label={startText.code}
        size="lg"
        value={code}
        onChange={(v) => setCode(v.toUpperCase())}
        autoFocus
        autoComplete="one-time-code"
      />
      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {error}
        </p>
      )}
    </StepFrame>
  );
}
